import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg, label, downloadFile, fmtDate } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, Panel, MatchRow, Empty, Spinner, StatusBadge, Table } from "../../components/kit";
import { DeviceSimulator, getPrefs, savePrefs } from "../../components/Device";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Slider } from "../../components/ui/slider";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const ROLE = { referee: "Referee", ar1: "Assistant Referee 1", ar2: "Assistant Referee 2", fourth: "Fourth Official", commissioner: "Match Commissioner", assessor: "Referee Assessor" };

export function Appointments() {
  const { me } = useAuth();
  const [list, setList] = useState(null);
  const load = () => api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data.filter((m) => Object.values(m.officials || {}).includes(me.user.id))));
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!list) return <Spinner />;
  return (
    <div>
      <PageHeader title="My Appointments" sub="Confirm appointments and open the match workspace" />
      <div className="space-y-3" data-testid="appointments-list">
        {!list.length && <Empty text="No appointments." />}
        {list.map((m) => {
          const roles = Object.entries(m.officials).filter(([, v]) => v === me.user.id).map(([k]) => k);
          const confirmed = roles.every((r) => m.confirmations?.[r]);
          return (
            <div key={m.id} className="tro-card flex flex-col gap-3 p-4 md:flex-row md:items-center" data-testid={`appointment-${m.match_number}`}>
              <div className="flex-1"><MatchRow m={m} /></div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#F7F8F9] px-3 py-1 text-xs font-bold">{roles.map((r) => ROLE[r]).join(", ")}</span>
                {!confirmed && <Button data-testid={`confirm-${m.match_number}`} onClick={async () => { try { await api.post(`/matches/${m.id}/confirm`); toast.success("Appointment confirmed"); load(); } catch (e) { toast.error(errMsg(e)); } }}>Confirm</Button>}
                {confirmed && <span className="text-xs font-bold text-[#1E7E34]">Confirmed</span>}
                {roles.includes("referee") ? <Link to={`/referee/matches/${m.id}/tactical`} data-testid={`enter-room-${m.match_number}`}><Button variant="outline">Enter Match Room</Button></Link>
                  : <Link to={`/officials/matches?match=${m.id}`}><Button variant="outline">Open workspace</Button></Link>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LiveMatches() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data)); }, []);
  if (!list) return <Spinner />;
  return (
    <div>
      <PageHeader title="Matches" sub="Live, upcoming and completed matches you are appointed to" />
      <div className="space-y-2">{list.length ? list.map((m) => <MatchRow key={m.id} m={m} to={m.officials?.referee ? `/referee/matches/${m.id}/tactical` : `/matches/${m.id}`} />) : <Empty />}</div>
    </div>
  );
}

export function RefereeReports() {
  const { me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [list, setList] = useState(null);
  const [f, setF] = useState({ incidents: "", match_notes: "", misconduct: "", pitch_conditions: "Good" });
  const [signed, setSigned] = useState(false);
  const [events, setEvents] = useState([]);
  const sel = sp.get("match");
  useEffect(() => {
    api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data.filter((m) => m.officials?.referee === me.user.id && !["scheduled", "awaiting_team_sheets", "teams_submitted", "teams_approved", "officials_confirmed", "ready_for_kickoff"].includes(m.status))));
  }, [me.user.id]);
  useEffect(() => { if (sel) api.get(`/matches/${sel}/events`).then((r) => setEvents(r.data)); }, [sel]);
  if (!list) return <Spinner />;
  const m = list.find((x) => x.id === sel);
  const count = (t) => events.filter((e) => e.status === "active" && t.includes(e.type)).length;
  const submit = async () => {
    try {
      await api.post(`/matches/${sel}/reports`, { type: "referee", signed, content: { final_score: `${m.home.name} ${m.score.home}-${m.score.away} ${m.away.name}`, goals: count(["goal", "own_goal", "penalty_scored"]), cards: count(["yellow", "second_yellow", "red"]), substitutions: count(["substitution"]), ...f } });
      toast.success("Referee report submitted");
      const r = await api.get("/matches", { params: { scope: "mine" } });
      setList(r.data.filter((x) => x.officials?.referee === me.user.id && list.some((y) => y.id === x.id)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <PageHeader title="Match Reports" sub="Submit signed referee reports after full time" />
      <Panel title="Matches">
        <div className="space-y-2">{list.length ? list.map((x) => (
          <button key={x.id} onClick={() => setSp({ match: x.id })} data-testid={`report-match-${x.match_number}`} className={`flex w-full items-center justify-between rounded-lg border p-3 text-left text-sm ${sel === x.id ? "border-[#17191C]" : "border-[#E6E9ED]"}`}>
            <b>{x.match_number} · {x.home.name} {x.score.home}–{x.score.away} {x.away.name}</b><StatusBadge status={x.status} />
          </button>)) : <Empty text="No completed matches yet." />}</div>
      </Panel>
      {m && (
        <Panel title={`Referee report · ${m.match_number}`} testid="referee-report-form" action={<Button variant="outline" data-testid="download-pdf-btn" onClick={() => downloadFile(`/matches/${m.id}/report.pdf`, `TRO_${m.match_number}.pdf`)}>Download PDF</Button>}>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[["Final score", `${m.score.home}–${m.score.away}`], ["Goals", count(["goal", "own_goal", "penalty_scored"])], ["Cards", count(["yellow", "second_yellow", "red"])], ["Substitutions", count(["substitution"])]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-[#F7F8F9] p-3"><div className="tro-label">{l}</div><div className="text-xl font-extrabold">{v}</div></div>))}
          </div>
          {["full_time", "reports_pending", "abandoned"].includes(m.status) ? (
            <div className="space-y-3">
              {[["incidents", "Incidents"], ["misconduct", "Misconduct details"], ["match_notes", "Match notes"]].map(([k, l]) => (
                <label key={k} className="block"><span className="tro-label">{l}</span><Textarea data-testid={`report-${k}`} className="mt-1" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>))}
              <label className="flex items-center gap-2 text-sm font-semibold"><Checkbox data-testid="report-signoff" checked={signed} onCheckedChange={(v) => setSigned(!!v)} />I, {me.user.name}, sign off this report as the official record.</label>
              <Button data-testid="submit-report-btn" disabled={!signed} className="h-12 font-extrabold" onClick={submit}>Submit signed report</Button>
            </div>
          ) : <div className="text-sm text-[#6C757D]" data-testid="report-status">Report status: {label(m.status)}</div>}
        </Panel>
      )}
    </div>
  );
}

export function Devices() {
  const [list, setList] = useState(null);
  const [prefs, setPrefs] = useState(getPrefs());
  const [mode, setMode] = useState("normal");
  const [f, setF] = useState({ name: "", serial: "" });
  const load = () => api.get("/devices").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { savePrefs(prefs); }, [prefs]);
  useEffect(() => {
    if (mode === "normal") return;
    const t = setTimeout(() => setMode("normal"), prefs.duration * 1000);
    return () => clearTimeout(t);
  }, [mode, prefs.duration]);
  if (!list) return <Spinner />;
  const d = list[0];
  const alert = { player: { name: "Test Player", number: 10, club: "TRO Test", photo: "/players/p2.jpg" }, minute: 45, second: 0 };
  const test = async () => {
    if (!d) return;
    await api.post(`/devices/${d.id}/test`, { color: "yellow" });
    await api.put(`/devices/${d.id}/settings`, { brightness: prefs.brightness, alert_duration: prefs.duration, flashing: prefs.flashing });
    setMode("yellow");
    setTimeout(() => setMode("red"), 1500);
    toast.success("Test command queued (SIMULATED adapter)");
    load();
  };
  return (
    <div className="space-y-6">
      <PageHeader title="TRO Referee One" sub="Device control & browser simulator — no physical hardware is connected" />
      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <Panel testid="device-sim-panel"><DeviceSimulator mode={mode} alert={alert} prefs={prefs} /></Panel>
        <div className="space-y-4">
          <Panel title="Simulator controls" testid="sim-controls">
            <div className="grid grid-cols-3 gap-2">
              <Button data-testid="sim-normal" variant="outline" className="h-12 font-bold" onClick={() => setMode("normal")}>Normal</Button>
              <Button data-testid="sim-yellow" className="h-12 bg-[#FFD400] font-bold text-[#17191C] hover:bg-[#f0c800]" onClick={() => setMode("yellow")}>Yellow</Button>
              <Button data-testid="sim-red" className="h-12 bg-[#F02030] font-bold hover:bg-[#d81a29]" onClick={() => setMode("red")}>Red</Button>
            </div>
            <div className="mt-4 space-y-4 text-sm">
              <div><div className="mb-2 flex justify-between"><span className="tro-label">Brightness</span><b>{prefs.brightness}%</b></div><Slider data-testid="sim-brightness" min={10} max={100} step={5} value={[prefs.brightness]} onValueChange={([v]) => setPrefs({ ...prefs, brightness: v })} /></div>
              <div><div className="mb-2 flex justify-between"><span className="tro-label">Alert duration</span><b>{prefs.duration}s</b></div><Slider data-testid="sim-duration" min={2} max={15} step={1} value={[prefs.duration]} onValueChange={([v]) => setPrefs({ ...prefs, duration: v })} /></div>
              <label className="flex items-center justify-between"><span className="font-semibold">Disable flashing</span><Switch data-testid="sim-disable-flashing" checked={!prefs.flashing} onCheckedChange={(v) => setPrefs({ ...prefs, flashing: !v })} /></label>
              <label className="flex items-center justify-between"><span className="font-semibold">Reduced motion</span><Switch data-testid="sim-reduced-motion" checked={prefs.reducedMotion} onCheckedChange={(v) => setPrefs({ ...prefs, reducedMotion: v })} /></label>
              <Button data-testid="sim-test-device" className="h-12 w-full font-bold" onClick={test} disabled={!d}>Test Device</Button>
            </div>
          </Panel>
          <Panel title="Device status" testid="device-status">
            {d ? <div className="space-y-1 text-sm">
              <div><b>{d.name}</b> · <span className="font-mono">{d.serial}</span></div>
              <div>Status: <b>{label(d.status)}</b> · Connection: <b>{label(d.connection)}</b> · Firmware {d.firmware}</div>
              <div className="text-xs text-[#6C757D]">Last test: {fmtDate(d.last_test) || "never"}</div>
              <div className="tro-label mt-3">Recent commands</div>
              {d.recent_commands.map((c) => <div key={c.id} className="text-xs">{fmtDate(c.created_at)} · {c.command} · {c.color} · {c.status}</div>)}
            </div> : <Empty text="No device registered." />}
          </Panel>
          <Panel title="Register device">
            <div className="space-y-2">
              <Input data-testid="device-name-input" placeholder="Device name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <Input data-testid="device-serial-input" placeholder="Serial number" value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} />
              <Button data-testid="device-register-btn" variant="outline" className="w-full" onClick={async () => { try { await api.post("/devices", f); toast.success("Device registered — pending review"); load(); } catch (e) { toast.error(errMsg(e)); } }}>Register</Button>
              <p className="text-[11px] text-[#6C757D]">Hardware adapter API: GET /api/devices/:id/commands · POST /api/devices/:id/commands/:cid/ack</p>
            </div>
          </Panel>
        </div>
      </div>
      <Table testid="all-devices" cols={[["Device", (x) => x.name], ["Serial", (x) => x.serial], ["Status", (x) => label(x.status)]]} rows={list.slice(1)} empty="No additional devices." />
    </div>
  );
}
